<template>
  <div
    ref="selectContainer"
    class="custom-select"
    :class="{ 'is-open': isOpen, 'has-error': hasError }"
  >
    <div
      ref="triggerRef"
      role="combobox"
      class="select-trigger"
      :class="{ 'is-open': isOpen, 'is-disabled': disabled }"
      tabindex="0"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-disabled="disabled"
      :aria-labelledby="formGroupLabelId"
      @click="toggleDropdown"
      @keydown="handleTriggerKeydown"
    >
      <span
        v-if="displayValue"
        class="select-value"
      >
        {{ displayValue }}
      </span>
      <span
        v-else
        class="select-placeholder"
      >
        {{ placeholder }}
      </span>
      <Icon
        class="select-arrow"
        :class="{ 'is-open': isOpen }"
        name="lucide:chevron-down"
        size="24"
        color="#000000"
      />
    </div>
    <div
      v-if="isOpen"
      ref="dropdownRef"
      class="dropdown-container"
      :class="{ 'is-open': isOpen }"
      role="listbox"
      :aria-label="placeholder"
      :aria-activedescendant="accessibleSelect.activeDescendantId.value"
      tabindex="0"
      @keydown="handleListboxKeydown"
      @focus="handleListboxFocus"
      @blur="handleListboxBlur"
    >
      <div
        v-if="searchable && optionList.length > 0"
        class="search-container"
        @click.stop
      >
        <div class="search-input-wrapper">
          <Icon
            class="search-icon"
            name="lucide:search"
            size="16"
            color="#2D2D2D"
          />
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="search-input"
            :placeholder="searchPlaceholder"
            @input="onSearchInput"
            @keydown.stop="handleSearchKeydown"
          >
        </div>
      </div>

      <div class="options-list">
        <div
          v-if="flattenedFilteredOptions.length === 0"
          class="no-options"
        >
          {{ noOptionsText }}
        </div>
        <template v-else>
          <div
            v-for="(group, groupIndex) in filteredGroupedOptions"
            :key="`group-${groupIndex}`"
            role="group"
            :aria-labelledby="groupedBy && group.label ? `group-label-${groupIndex}` : void 0"
            :aria-label="!groupedBy || !group.label ? `Group ${groupIndex + 1}` : void 0"
          >
            <div
              v-if="groupedBy && group.label"
              :id="`group-label-${groupIndex}`"
              class="option-group-label"
            >
              {{ group.label }}
            </div>
            <div
              v-for="(option, optionIndex) in group.options"
              :id="`option-${getFlatIndex(groupIndex, optionIndex)}`"
              :key="getOptionKey(option, getFlatIndex(groupIndex, optionIndex))"
              class="option-item"
              :class="{
  'is-focused': accessibleSelect.focusedIndex.value === getFlatIndex(groupIndex, optionIndex),
  'is-selected': isSelected(option)
}"
              role="option"
              :aria-selected="isSelected(option)"
              :aria-label="getOptionLabel(option).toString()"
              @click="selectOption(option)"
              @mouseenter="accessibleSelect.setFocusedIndex(getFlatIndex(groupIndex, optionIndex), flattenedFilteredOptions.length)"
            >
              <Icon
                v-if="isSelected(option)"
                class="option-checkmark"
                name="lucide:check"
                size="16"
                color="#1850C1"
              />
              <span class="option-text">
                {{ getOptionLabel(option) }}
              </span>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted, useTemplateRef } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useAccessibleSelect } from "../../composables/useAccessibleSelect";
const props = defineProps({
  modelValue: { type: null, required: false, default: void 0 },
  options: { type: Array, required: false, default: () => [] },
  trackBy: { type: [String, Function], required: false, default: "value" },
  labelBy: { type: String, required: false, default: "label" },
  groupedBy: { type: String, required: false, default: void 0 },
  placeholder: { type: String, required: false, default: "Select an option" },
  searchable: { type: Boolean, required: false, default: false },
  searchPlaceholder: { type: String, required: false, default: "Search..." },
  noOptionsText: { type: String, required: false, default: "No options available" },
  disabled: { type: Boolean, required: false, default: false },
  hasError: { type: Boolean, required: false, default: false },
  formGroupLabelId: { type: String, required: false }
});
const emit = defineEmits(["update:modelValue", "option-selected", "search"]);
const selectContainer = useTemplateRef("selectContainer");
const triggerRef = useTemplateRef("triggerRef");
const dropdownRef = useTemplateRef("dropdownRef");
const searchInputRef = useTemplateRef("searchInputRef");
const isOpen = ref(false);
const searchQuery = ref("");
const accessibleSelect = useAccessibleSelect({
  onClose: closeDropdown,
  scrollBehavior: "smooth",
  typeAheadTimeout: 500,
  wrapNavigation: true
});
onClickOutside(selectContainer, () => {
  if (isOpen.value) {
    closeDropdown();
  }
});
const displayValue = computed(() => {
  if (props.modelValue === void 0) {
    return "";
  }
  if (typeof props.modelValue === "object") {
    return getOptionLabel(props.modelValue);
  }
  const option = (props.options || []).find((opt) => getOptionTrackValue(opt) === props.modelValue);
  return option ? getOptionLabel(option) : props.modelValue;
});
const optionList = computed(() => {
  return props.options || [];
});
const groupedOptions = computed(() => {
  if (!props.groupedBy) {
    return [{
      label: "",
      options: optionList.value
    }];
  }
  const groups = /* @__PURE__ */ new Map();
  optionList.value.forEach((option) => {
    const groupValue = typeof option === "object" && option ? option[props.groupedBy] : "";
    const groupKey = String(groupValue || "Other");
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey).push(option);
  });
  return Array.from(groups.entries()).map(([label, options]) => ({
    label,
    options
  }));
});
const filteredGroupedOptions = computed(() => {
  if (!props.searchable || !searchQuery.value.trim()) {
    return groupedOptions.value;
  }
  const query = searchQuery.value.toLowerCase().trim();
  return groupedOptions.value.map((group) => ({
    label: group.label,
    options: group.options.filter(
      (option) => getOptionLabel(option).toString().toLowerCase().includes(query)
    )
  })).filter((group) => group.options.length > 0);
});
const flattenedFilteredOptions = computed(() => {
  return filteredGroupedOptions.value.flatMap((group) => group.options);
});
watch(() => props.options, () => {
  searchQuery.value = "";
  accessibleSelect.clearFocusedIndex();
});
onMounted(() => {
  if (props.formGroupLabelId) {
    const formGroupLabel = document.getElementById(props.formGroupLabelId);
    formGroupLabel?.addEventListener("click", handleFormGroupLabelClick);
  }
});
onUnmounted(() => {
  if (props.formGroupLabelId) {
    const formGroupLabel = document.getElementById(props.formGroupLabelId);
    formGroupLabel?.removeEventListener("click", handleFormGroupLabelClick);
  }
});
function handleFormGroupLabelClick() {
  if (!isOpen.value) {
    openDropdown();
  }
}
function getOptionLabel(option) {
  if (typeof option === "string") {
    return option;
  }
  if (typeof option === "object" && option) {
    return option[props.labelBy];
  }
  return option;
}
function getOptionTrackValue(option) {
  if (typeof option === "string") {
    return option;
  }
  if (typeof option === "object" && option !== null) {
    return option[props.trackBy.toString()] || option.value || option.id;
  }
  return option;
}
function getOptionKey(option, index) {
  const trackValue = getOptionTrackValue(option);
  return `${trackValue}-${index}`;
}
function getFlatIndex(groupIndex, optionIndex) {
  let flatIndex = 0;
  for (let i = 0; i < groupIndex; i++) {
    flatIndex += filteredGroupedOptions.value[i].options.length;
  }
  return flatIndex + optionIndex;
}
function isSelected(option) {
  if (props.modelValue === void 0) {
    return false;
  }
  const optionTrackValue = getOptionTrackValue(option);
  if (typeof props.modelValue === "object") {
    return getOptionTrackValue(props.modelValue) === optionTrackValue;
  }
  return props.modelValue === optionTrackValue;
}
function toggleDropdown() {
  if (props.disabled) return;
  if (isOpen.value) {
    closeDropdown();
  } else {
    openDropdown();
  }
}
function openDropdown() {
  if (props.disabled) {
    return;
  }
  isOpen.value = true;
  nextTick(() => {
    if (dropdownRef.value) {
      dropdownRef.value.focus();
      accessibleSelect.initializeFocus(flattenedFilteredOptions.value, "option", (option) => isSelected(option));
    }
    if (props.searchable && searchInputRef.value) {
      searchInputRef.value.focus();
    }
  });
}
function closeDropdown() {
  isOpen.value = false;
  searchQuery.value = "";
  accessibleSelect.clearFocusedIndex();
  nextTick(() => {
    if (triggerRef.value) {
      triggerRef.value.focus();
    }
  });
}
function selectOption(option) {
  const trackValue = getOptionTrackValue(option);
  if (trackValue === props.modelValue) {
    emit("update:modelValue", void 0);
    emit("option-selected", void 0);
  } else {
    emit("update:modelValue", trackValue);
    emit("option-selected", option);
  }
  closeDropdown();
}
function handleTriggerKeydown(event) {
  accessibleSelect.handleTriggerKeydown(event, isOpen.value, toggleDropdown);
}
function handleListboxKeydown(event) {
  const config = {
    onSelect: (index) => {
      const option = flattenedFilteredOptions.value[index];
      if (option) {
        selectOption(option);
      }
    },
    returnFocusSelector: ".select-trigger",
    optionNameGetter: (option) => getOptionTrackValue(option),
    allowMultiSelect: false
  };
  accessibleSelect.handleListboxKeydown(event, flattenedFilteredOptions.value, "option", config);
}
function handleListboxFocus() {
  accessibleSelect.handleListboxFocus(flattenedFilteredOptions.value, "option", (option) => isSelected(option));
}
function handleListboxBlur(event) {
  accessibleSelect.handleListboxBlur(
    event,
    selectContainer,
    closeDropdown,
    [".search-input", ".option-item"]
  );
}
function handleSearchKeydown(event) {
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    if (dropdownRef.value) {
      dropdownRef.value.focus();
      if (event.key === "ArrowDown") {
        accessibleSelect.setFocusedIndex(0, flattenedFilteredOptions.value.length);
      }
    }
  } else if (event.key === "Escape") {
    closeDropdown();
  } else if (event.key === "Enter") {
    event.preventDefault();
    if (flattenedFilteredOptions.value.length > 0) {
      selectOption(flattenedFilteredOptions.value[0]);
    }
  }
}
function onSearchInput() {
  emit("search", searchQuery.value);
  accessibleSelect.clearFocusedIndex();
}
defineExpose({
  open: openDropdown,
  close: closeDropdown,
  focus: () => triggerRef.value?.focus()
});
</script>

<style scoped>
.custom-select {
  --border-radius-xs: 4px;
  --color-border-default: #ADADAD;
  --color-white: #FFFFFF;
  --color-grey-50: #F7F7F7;
  --color-blue-75: #E6EDFA;
  --color-blue-500: #1850C1;
  --color-grey-100: #E0E0E0;
  --color-grey-200: #D3D3D3;
  --color-grey-600: #757575;
  --color-grey-950: #0b0a0a;
  --color-surface-danger-default: #FDF5F4;
  --color-text-secondary: #616161;
  --color-text-severity-danger-on-bg: #9B2A1D;
  --color-text-tertiary: #949494;
  --color-white: #FFF;
  --space-400: 16px;
  position: relative;
  width: 100%;
  box-sizing: border-box;
}

.select-trigger {
  padding: 12px;
  border: 1px solid var(--color-border-default);
  border-radius: 4px;
  background: var(--color-white);
  color: var(--color-grey-950);
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: border-color 0.2s;
  text-align: left;
}

.select-trigger:focus:not(.is-disabled),
.select-trigger.is-open {
  border-color: var(--color-blue-500);
  outline: 1px solid var(--color-blue-500);
}

.select-trigger.is-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.custom-select.has-error .select-trigger {
  border-color: var(--color-text-severity-danger-on-bg);
  background: var(--color-surface-danger-default);
  color: var(--color-text-severity-danger-on-bg);
}

.select-value {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.select-placeholder {
  color: var(--color-text-secondary);
}

.select-arrow {
  flex-shrink: 0;
  transition: transform 0.2s;
  color: var(--color-text-secondary);
}

.select-arrow.is-open {
  transform: rotate(180deg);
}

.dropdown-container {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: var(--color-white);
  border: 1px solid var(--color-border-default);
  border-radius: var(--border-radius-xs);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 8px;
}

.dropdown-container.is-open {
  outline: none;
}

.search-container {
  padding: 16px 16px 6px 16px;
  border-bottom: 1px solid var(--color-grey-100);
  box-sizing: border-box;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  z-index: 1;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 40px;
  padding: 12px 0 12px 36px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-grey-950);
  border-radius: var(--border-radius-xs);
  border: 1px solid var(--color-grey-200);
  background: var(--color-grey-50);
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
}

.search-input::placeholder {
  color: var(--color-text-tertiary);
}

.options-list {
  max-height: 200px;
  overflow-y: auto;
  scrollbar-width: thin;
  box-shadow: 0 -6px 20px 0 rgba(0, 0, 0, 0.06);
}

.no-options {
  padding: var(--space-300);
  color: var(--color-text-secondary);
  font-size: 14px;
  text-align: center;
}

.option-item {
  position: relative;
  padding: 12px 16px 12px 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s;
  border: none;
  background: #ffffff;
}

.option-item:hover,
.option-item.is-focused {
  background: var(--color-blue-75);
}

.option-item.is-selected {
  background: var(--color-blue-75);
  color: var(--color-blue-500);
}

.option-text {
  flex: 1;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  color: var(--color-grey-950);
  letter-spacing: 0.1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.option-checkmark {
  position: absolute;
  left: 16px;
}

/* Scrollbar styling */
.options-list::-webkit-scrollbar {
  width: 6px;
}

.options-list::-webkit-scrollbar-track {
  background: transparent;
}

.options-list::-webkit-scrollbar-thumb {
  background: var(--color-grey-100);
  border-radius: 3px;
}

.options-list::-webkit-scrollbar-thumb:hover {
  background: var(--color-border-default);
}

.option-group-label {
  padding: 8px 16px 4px 16px;
  font-family: "Neue Haas Grotesk Display Pro", sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: var(--color-grey-600);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0;
}
</style>
